import React from "react";
import { PlxsButton } from "@plexlogic/plexus-ui";
import { toSentenceCase } from "../utils/stringUtils";

interface WizardRefreshButtonProps {
  /**
   * Click handler for refresh action
   */
  onClick: () => void;
  
  /**
   * Whether the button is disabled
   */
  isDisabled?: boolean;
  
  /**
   * Test ID for testing purposes
   */
  dataTestId?: string;
  
  /**
   * Whether to show icon only (no label)
   * Default: false (shows both icon and label)
   */
  iconOnly?: boolean;
  
  /**
   * Optional custom label (defaults to "Refresh")
   */
  label?: string;
}

/**
 * Standardized Refresh Button Component
 * 
 * Provides consistent styling for refresh buttons across the application.
 * Uses neutral tone for subtle appearance (secondary action pattern).
 * 
 * Design decisions:
 * - variant="secondary" - Refresh is a secondary action
 * - tone="neutral" - Subtle appearance, doesn't compete with primary actions
 * - functionalIcon="sync" - Standard refresh icon
 * - Supports iconOnly mode for compact layouts
 * 
 * @example
 * // Default usage (with label)
 * <WizardRefreshButton onClick={handleRefresh} />
 * 
 * @example
 * // Icon only mode
 * <WizardRefreshButton onClick={handleRefresh} iconOnly />
 * 
 * @example
 * // Custom label
 * <WizardRefreshButton onClick={handleRefresh} label="Reload Data" />
 */
const WizardRefreshButton: React.FC<WizardRefreshButtonProps> = ({
  onClick,
  isDisabled = false,
  dataTestId = "refresh-button",
  iconOnly = false,
  label,
}) => {
  // For icon-only mode, use empty string for label
  // For regular mode, use provided label or default to "Refresh"
  const buttonLabel = iconOnly ? "" : (label || toSentenceCase("refresh"));
  
  return (
    <PlxsButton
      variant="secondary"
      tone="neutral"
      functionalIcon="sync"
      isIconOnly={iconOnly}
      label={buttonLabel}
      onClick={onClick}
      isDisabled={isDisabled}
      dataTestId={dataTestId}
    />
  );
};

export default WizardRefreshButton;
